<div class="agreement" data-readiness-target="price-agreement">
  @if (form(); as f) {
  <!-- ── Create / edit form ─────────────────────────────────────────── -->
  <div class="agreement__preview-block" data-testid="price-agreement-form">
    <header class="agreement__preview-meta">
      <mat-icon aria-hidden="true"
        >{{ f.mode === "create" ? "add" : "edit" }}</mat-icon
      >
      @if (f.mode === "create") { New T&amp;C for
      <strong>{{ priceName() }}</strong>
      } @else { Edit ·
      <strong>{{ effectiveName() }}</strong>
      <code class="agreement__id">{{ f.id }}</code>
      }
    </header>

    @if (f.mode === "edit") { @if (usedBy(); as used) {
    <p class="agreement__hint" data-testid="price-agreement-edit-reach">
      <mat-icon aria-hidden="true">info</mat-icon>
      Saving updates this T&amp;C everywhere it is used: {{ used.count }} of {{
      used.total }} prices here, and any other package that uses it.
    </p>
    } }

    <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <enr-input
        [size]="'large'"
        [label]="'Type'"
        placeholder="package"
        data-testid="price-agreement-form-type"
        [value]="draft().agreementType"
        (valueChange)="setDraftField('agreementType', $event)"
      ></enr-input>
      <enr-input
        [size]="'large'"
        [label]="'Status'"
        placeholder="active"
        data-testid="price-agreement-form-status"
        [value]="draft().status"
        (valueChange)="setDraftField('status', $event)"
      ></enr-input>
    </div>

    @for (l of languages(); track l.locale) {
    <section
      class="flex flex-col gap-3 rounded-lg border border-[#e8ebe4] p-4"
      [attr.data-testid]="'price-agreement-form-' + l.locale"
    >
      <header class="flex items-center gap-2 text-[0.82rem]">
        <span class="agreement__tag">{{ l.shortCode }}</span>
        <span class="text-[#525a68]">{{ l.label }}</span>
      </header>
      <enr-input
        [size]="'large'"
        [label]="'Name'"
        [placeholder]="'Name in ' + l.label"
        [value]="draft().name[l.locale] ?? ''"
        (valueChange)="setDraftName(l.locale, $event)"
      ></enr-input>
      <div class="flex flex-col gap-1.5">
        <span class="agreement__label">T&amp;C text</span>
        <term-rich-editor
          [value]="draft().description[l.locale] ?? ''"
          [placeholder]="'Write the T&C in ' + l.label"
          (valueChange)="setDraftDescription(l.locale, $event)"
        ></term-rich-editor>
      </div>
    </section>
    } @if (!canSave()) {
    <p
      class="m-0 text-[0.8rem] text-[#b45309]"
      role="alert"
      data-testid="price-agreement-form-required"
    >
      {{ f.mode === "create" ? "Enter a name and the T&C text in at least one
      language." : "Enter a name in at least one language." }}
    </p>
    } @if (saveFailed()) {
    <p
      class="m-0 text-[0.8rem] text-[#b42318]"
      role="alert"
      data-testid="price-agreement-form-error"
    >
      Couldn't save the T&amp;C. Check your connection and try again.
    </p>
    }

    <div class="flex flex-wrap justify-end gap-2">
      <enr-button
        variant="text"
        size="small"
        data-testid="price-agreement-form-cancel"
        [disabled]="saving()"
        (clicked)="closeForm()"
      >
        Cancel
      </enr-button>
      <enr-button
        variant="primary"
        size="small"
        data-testid="price-agreement-form-save"
        [loading]="saving()"
        [disabled]="!canSave() || saving()"
        (clicked)="save()"
      >
        {{ f.mode === "create" ? "Create and use for this price" : "Save
        changes" }}
      </enr-button>
    </div>
  </div>
  } @else {
  <!-- ── Picker row (same shape as the Term tab) ───────────────────── -->
  <div class="agreement__pick">
    <div class="agreement__pick-field">
      <span class="agreement__label">Agreement</span>
      <enr-select
        #picker
        class="block w-full"
        [size]="'large'"
        data-testid="price-agreement-select"
        ariaLabel="Agreement for this price"
        placeholder="Choose an Agreement"
        [searchable]="true"
        searchPlaceholder="Search Agreements…"
        [options]="options()"
        [value]="currentValue()"
        (valueChange)="choose($event)"
        (stateChange)="search.onState($event, picker)"
        (searchChange)="search.onSearch($event)"
      ></enr-select>
    </div>
    <enr-button
      variant="outline"
      size="large"
      [leadingIcon]="true"
      data-testid="price-agreement-create"
      (clicked)="openCreate()"
    >
      <mat-icon slot="leading-icon" aria-hidden="true">add</mat-icon>
      New T&amp;C
    </enr-button>
  </div>

  @if (effective(); as eff) {
  <!-- ── Preview (read-only) ─────────────────────────────────────── -->
  <div class="agreement__preview-block" data-testid="price-agreement-card">
    <header class="flex flex-wrap items-center gap-x-3 gap-y-1">
      <div class="agreement__preview-meta flex-1">
        <mat-icon aria-hidden="true">visibility</mat-icon>
        Preview ·
        <strong>{{ effectiveName() }}</strong>
        <code class="agreement__id">{{ eff.id }}</code>
        @if (source() === "own") {
        <span
          class="agreement__tag agreement__tag--own"
          data-testid="price-agreement-source"
          >Only this price</span
        >
        } @else {
        <span class="agreement__tag" data-testid="price-agreement-source"
          >Package T&amp;C</span
        >
        }
      </div>
      <enr-button
        variant="text"
        size="small"
        [leadingIcon]="true"
        data-testid="price-agreement-edit"
        [disabled]="!canEdit()"
        (clicked)="openEdit()"
      >
        <mat-icon slot="leading-icon" aria-hidden="true">edit</mat-icon>
        Edit this T&amp;C
      </enr-button>
    </header>

    <div class="flex flex-wrap items-center gap-3">
      <div class="agreement__locales" role="tablist" aria-label="Language">
        @for (l of languages(); track l.locale) {
        <button
          type="button"
          role="tab"
          class="agreement__locale"
          [class.agreement__locale--on]="locale() === l.locale"
          [attr.aria-selected]="locale() === l.locale"
          (click)="setLocale(l.locale)"
        >
          {{ l.shortCode }}
        </button>
        }
      </div>
      @if (usedBy(); as used) {
      <span class="agreement__used-by" data-testid="price-agreement-used-by"
        >Used by {{ used.count }} of {{ used.total }} prices</span
      >
      }
    </div>

    @if (effectiveDetail(); as a) { @if (a.agreementType || a.status ||
    a.initialDate) {
    <dl class="agreement__meta">
      @if (a.agreementType) {
      <div class="agreement__meta-row">
        <dt>Type</dt>
        <dd>{{ a.agreementType }}</dd>
      </div>
      } @if (a.status) {
      <div class="agreement__meta-row">
        <dt>Status</dt>
        <dd>{{ a.status }}</dd>
      </div>
      } @if (a.initialDate) {
      <div class="agreement__meta-row">
        <dt>Initial date</dt>
        <dd>{{ a.initialDate | date: "mediumDate" }}</dd>
      </div>
      }
    </dl>
    } } @if (previewLoading()) {
    <div
      class="flex flex-col gap-2.5 py-1.5"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <span
        class="h-2.75 animate-pulse rounded-md bg-[#eef1ea] motion-reduce:animate-none"
        aria-hidden="true"
      ></span>
      <span
        class="h-2.75 w-[90%] animate-pulse rounded-md bg-[#eef1ea] motion-reduce:animate-none"
        aria-hidden="true"
      ></span>
      <span
        class="h-2.75 w-3/4 animate-pulse rounded-md bg-[#eef1ea] motion-reduce:animate-none"
        aria-hidden="true"
      ></span>
      <span
        class="inline-flex items-center gap-2 text-[0.82rem] text-[#525a68]"
        data-testid="price-agreement-loading"
      >
        <span
          class="size-3.5 shrink-0 animate-spin rounded-full border-2 border-[#e8ebe4] border-t-[#8bc129] motion-reduce:animate-none"
          aria-hidden="true"
        ></span>
        Loading T&amp;C text…
      </span>
    </div>
    } @else if (previewHtml(); as html) {
    <div
      class="agreement__preview"
      tabindex="0"
      aria-label="T&C text"
      data-testid="price-agreement-preview"
      [innerHTML]="html"
    ></div>
    } @else {
    <p class="agreement__preview-empty">Not authored in this language yet.</p>
    } @if (source() === "offering") {
    <p class="agreement__hint">
      <mat-icon aria-hidden="true">link</mat-icon>
      Follows Package settings — changing it there updates every price that uses
      the package T&amp;C.
    </p>
    } @if (ownMatchesOffering()) {
    <p class="agreement__hint" data-testid="price-agreement-fixed-note">
      <mat-icon aria-hidden="true">push_pin</mat-icon>
      This is the package T&amp;C, fixed on this price — changes on the Term tab
      won't reach it.
    </p>
    }
  </div>
  } @else {
  <div class="agreement__missing" data-testid="price-agreement-missing">
    <mat-icon aria-hidden="true" class="agreement__missing-icon"
      >warning_amber</mat-icon
    >
    <div class="agreement__missing-text">
      <strong>No T&amp;C for this price</strong>
      <span
        >The package has none either. Choose an Agreement above, create one, or
        set the package T&amp;C on the Term tab.</span
      >
    </div>
  </div>
  } }
</div>
